/**
 * Product row: progress hint and arrows over the section's native scroll-snap
 * viewport. The browser does all the moving, at every width, so a swipe is as
 * smooth as any native scroll. Under 700px with more than two cards the row is
 * endless: a copy of the set sits on each side (hidden from assistive tech and
 * the tab order, but tappable), and once scrolling has fully stopped with no
 * finger down, a copy in the centre is swapped for the same real card by
 * moving the viewport one set width without animation. The content under the
 * swap is identical, so it never shows. Card photos are loaded and decoded as
 * the row nears the screen, so none pops in mid-swipe.
 */
(() => {
if (customElements.get('product-callout')) return;
const LOOP = matchMedia('(max-width: 700px)');
const REDUCED_MOTION = matchMedia('(prefers-reduced-motion: reduce)');
const SETTLE = 150; // ms without scroll events that count as stopped; scrollend, where there is one, comes sooner

class ProductCallout extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.product-callout__viewport');
    this.track = this.querySelector('.product-callout__track');
    this.hint = this.querySelector('.product-callout__hint');
    this.prev = this.querySelector('.product-callout__arrow--prev');
    this.next = this.querySelector('.product-callout__arrow--next');
    if (!this.viewport || !this.track) return;

    this.events = new AbortController();
    const signal = this.events.signal;
    this.update = this.update.bind(this);
    this.sync = this.sync.bind(this);
    this.settle = this.settle.bind(this);
    this.viewport.addEventListener('scroll', () => {
      this.update();
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(this.settle, SETTLE);
    }, { signal, passive: true });
    this.viewport.addEventListener('scrollend', this.settle, { signal, passive: true });
    // Never swap under a finger: a still finger mid-drag stops the scroll events too.
    this.viewport.addEventListener('touchstart', () => { this.touching = true; }, { signal, passive: true });
    const lift = (event) => {
      if (event.touches.length) return;
      this.touching = false;
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(this.settle, SETTLE);
    };
    // The last finger may lift outside the row.
    window.addEventListener('touchend', lift, { signal, passive: true });
    window.addEventListener('touchcancel', lift, { signal, passive: true });
    // Snapping alone leaves a card reached by Tab at the edge of the row.
    this.track.addEventListener('focusin', (event) => {
      if (!event.target.matches(':focus-visible')) return;
      const card = this.slides.find((slide) => slide.contains(event.target));
      // After the browser's own reveal of the focused link, which would otherwise cut this scroll short.
      if (card) requestAnimationFrame(() => this.reveal(card));
    }, { signal });
    this.prev?.addEventListener('click', () => this.step(-1), { signal });
    this.next?.addEventListener('click', () => this.step(1), { signal });
    LOOP.addEventListener('change', this.sync, { signal });
    this.resizer = new ResizeObserver(this.sync);
    this.resizer.observe(this.viewport);
    if (this.cards[0]) this.resizer.observe(this.cards[0]);
    this.preloader = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.preloader.disconnect();
      this.preloaded = true;
      this.preload();
    }, { rootMargin: '100% 0px' });
    this.preloader.observe(this);
    this.sync();
  }

  disconnectedCallback() {
    this.events?.abort();
    this.resizer?.disconnect();
    this.preloader?.disconnect();
    clearTimeout(this.settleTimer);
  }

  get slides() {
    return Array.from(this.track.children);
  }

  get cards() {
    return this.slides.filter((slide) => !slide.hasAttribute('data-clone'));
  }

  preload() {
    this.track.querySelectorAll('img').forEach((img) => {
      img.loading = 'eager';
      img.decode().catch(() => {});
    });
  }

  /* Adds or removes the copies for the current width and re-measures, keeping the centred card. */
  sync() {
    const cards = this.cards;
    const loop = LOOP.matches && cards.length > 2;
    const card = this.measured ? this.index() : 0;
    if (loop !== Boolean(this.looping)) {
      this.looping = loop;
      if (loop) {
        this.track.prepend(...cards.map((item) => this.copy(item)));
        this.track.append(...cards.map((item) => this.copy(item)));
        if (this.preloaded) this.preload();
      } else {
        this.track.querySelectorAll(':scope > [data-clone]').forEach((clone) => clone.remove());
        this.viewport.scrollLeft = 0;
      }
    }
    this.count = cards.length;
    this.measured = false;
    if (this.looping) {
      const slides = this.slides;
      const viewport = this.viewport.getBoundingClientRect();
      const first = slides[0].getBoundingClientRect();
      this.cardWidth = first.width;
      this.stepWidth = slides[1].getBoundingClientRect().left - first.left;
      this.firstLeft = first.left - viewport.left + this.viewport.scrollLeft;
      // Before the section's styles apply the slides are not in a row yet; the resize observer calls again.
      this.measured = this.cardWidth > 0 && this.stepWidth > 0;
      // A size change under a finger is left to the browser's re-snap and to settle() after lifting.
      if (this.measured && !this.touching) this.jump(this.count + card);
    }
    this.update();
  }

  copy(card) {
    const copy = card.cloneNode(true);
    copy.setAttribute('data-clone', '');
    copy.setAttribute('aria-hidden', 'true');
    copy.removeAttribute('id');
    copy.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
    copy.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => { node.tabIndex = -1; });
    return copy;
  }

  /* Index among all slides (copies included) of the card nearest the centre, from scrollLeft alone. */
  centred() {
    const centre = this.viewport.scrollLeft + this.viewport.clientWidth / 2;
    return Math.round((centre - this.firstLeft - this.cardWidth / 2) / this.stepWidth);
  }

  /* Position of the centred card within the real set. */
  index() {
    if (!this.measured) return 0;
    return ((this.centred() % this.count) + this.count) % this.count;
  }

  /* Centres slide i at once. */
  jump(i) {
    const left = this.firstLeft + i * this.stepWidth + this.cardWidth / 2 - this.viewport.clientWidth / 2;
    this.viewport.scrollTo({ left, behavior: 'instant' });
  }

  /* Scrolling has stopped: a copy in the centre becomes the same real card. */
  settle() {
    clearTimeout(this.settleTimer);
    if (!this.measured || this.touching) return;
    const i = this.centred();
    if (i < this.count || i >= 2 * this.count) this.jump(this.count + this.index());
  }

  /* Brings a card to its snap position: centred on phones, at the start of the row wider up.
     scrollIntoView is not used: WebKit ignores it for a card already partly in view. */
  reveal(card) {
    const viewport = this.viewport.getBoundingClientRect();
    const rect = card.getBoundingClientRect();
    const offset = LOOP.matches ? (viewport.width - rect.width) / 2 : parseFloat(getComputedStyle(this.viewport).scrollPaddingLeft) || 0;
    this.viewport.scrollTo({ left: this.viewport.scrollLeft + rect.left - viewport.left - offset, behavior: REDUCED_MOTION.matches ? 'instant' : 'smooth' });
  }

  step(direction) {
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const width = this.slides[0].getBoundingClientRect().width + gap;
    this.viewport.scrollBy({ left: direction * width, behavior: REDUCED_MOTION.matches ? 'instant' : 'smooth' });
  }

  update() {
    const { clientWidth, scrollLeft, scrollWidth } = this.viewport;
    this.toggleAttribute('data-scrollable', scrollWidth > clientWidth + 1);
    if (this.looping) {
      this.hint?.style.setProperty('--product-callout-hint-width', `${100 / this.count}%`);
      this.hint?.style.setProperty('--product-callout-hint-left', `${(this.index() / this.count) * 100}%`);
      if (this.prev) this.prev.disabled = false;
      if (this.next) this.next.disabled = false;
      return;
    }
    this.hint?.style.setProperty('--product-callout-hint-width', `${Math.min(100, ((clientWidth + scrollLeft) / scrollWidth) * 100)}%`);
    this.hint?.style.setProperty('--product-callout-hint-left', '0%');
    if (this.prev) this.prev.disabled = scrollLeft <= 0;
    if (this.next) this.next.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
  }
}

customElements.define('product-callout', ProductCallout);
})();
